import { cn } from "@/lib/utils";

interface ShimmerSkeletonProps {
  className?: string;
  /** Number of skeleton items to render */
  count?: number;
  /** Delay between each skeleton animation start (for stagger effect) */
  staggerDelay?: number;
}

/**
 * Enhanced skeleton with shimmer animation.
 * Use for loading states with a polished appearance.
 */
export const ShimmerSkeleton: React.FC<ShimmerSkeletonProps> = ({
  className,
  count = 1,
  staggerDelay = 100,
}) => {
  if (count === 1) {
    return <div className={cn("rounded-lg bg-muted shimmer", className)} />;
  }

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className={cn("rounded-lg bg-muted shimmer", className)}
          style={{
            animationDelay: `${index * staggerDelay}ms`,
          }}
        />
      ))}
    </>
  );
};

/**
 * Bank row skeleton for institution list loading state
 */
export const BankRowSkeleton: React.FC<{ index?: number }> = ({
  index = 0,
}) => (
  <div
    className="flex items-center gap-3 p-3 animate-fade-in-up"
    style={{ animationDelay: `${index * 50}ms` }}
  >
    <ShimmerSkeleton className="h-12 w-12 rounded-xl flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <ShimmerSkeleton className="h-4 w-32 rounded" />
      <ShimmerSkeleton className="h-3 w-24 rounded" />
    </div>
    <ShimmerSkeleton className="h-5 w-5 rounded" />
  </div>
);

/**
 * Account card skeleton for account selection loading state
 */
export const AccountCardSkeleton: React.FC<{ index?: number }> = ({
  index = 0,
}) => (
  <div
    className="rounded-xl border-2 border-transparent p-4 animate-fade-in-up"
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <ShimmerSkeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-2">
          <ShimmerSkeleton className="h-4 w-28 rounded" />
          <ShimmerSkeleton className="h-3 w-20 rounded" />
        </div>
      </div>
      <ShimmerSkeleton className="h-6 w-6 rounded-full" />
    </div>
    <div className="mt-3 flex gap-2">
      <ShimmerSkeleton className="h-6 w-20 rounded-full" />
      <ShimmerSkeleton className="h-6 w-24 rounded-full" />
    </div>
  </div>
);

/**
 * Form field skeleton for authentication loading
 */
export const FormFieldSkeleton: React.FC<{ index?: number }> = ({
  index = 0,
}) => (
  <div
    className="space-y-2 animate-fade-in-up"
    style={{ animationDelay: `${index * 80}ms` }}
  >
    <ShimmerSkeleton className="h-4 w-20 rounded" />
    <ShimmerSkeleton className="h-11 w-full rounded-lg" />
  </div>
);

export default ShimmerSkeleton;
